import React, { useEffect, useMemo, useState } from 'react';
import { StepContainer, NavigationButtons, InfoBox, RadioGroup } from '../index';
import { useWizard } from '../../wizard';
import { contentApi } from '../../services/contentApi';
import { contentKeys } from '../../constants/contentKeys';

interface ErrorState {
  custody?: string;
}

const custodyOptions: Array<{ value: 'ene' | 'med'; label: string }> = [
  { value: 'ene', label: 'Jeg har ene forældremyndighed (kun én underskrift)' },
  { value: 'med', label: 'Vi har fælles forældremyndighed (begge forældre underskriver)' }
];

const fallbackCopy = [
  'MitID-login’et identificerer dig som første underskriver af samtykket (P23T).',
  'Hvis I er to forældre eller værger, kan du vælge om vi skal sende digital post til den anden i næste trin.'
];

const DriverConsentGuardianStep: React.FC = () => {
  const { selfServiceData, updateSelfServiceData, goNext, goBack } = useWizard();
  const [errors, setErrors] = useState<ErrorState>({});
  const [copy, setCopy] = useState<string[] | null>(null);

  const custodyValue = selfServiceData.dlCustodyType ?? '';
  const isSharedCustody = custodyValue === 'med';

  const fallbackGuardianName = selfServiceData.user?.name ?? '';
  const fallbackGuardianEmail = selfServiceData.user?.email ?? '';

  const primaryGuardianSummary = useMemo(() => {
    const name = selfServiceData.dlGuardian1Name?.trim() || fallbackGuardianName;
    const email = selfServiceData.dlGuardian1Email?.trim() || fallbackGuardianEmail;
    if (name && email) {return `${name} (${email})`;}
    if (name) {return name;}
    if (email) {return email;}
    return 'Den forælder/værge der er logget ind med MitID';
  }, [selfServiceData.dlGuardian1Name, selfServiceData.dlGuardian1Email, fallbackGuardianName, fallbackGuardianEmail]);

  useEffect(() => {
    if (selfServiceData.dlParentConsentRequested !== true) {
      updateSelfServiceData('dlParentConsentRequested', true);
    }
    if (selfServiceData.dlUnder18 !== true) {
      updateSelfServiceData('dlUnder18', true);
    }
  }, [selfServiceData.dlParentConsentRequested, selfServiceData.dlUnder18, updateSelfServiceData]);

  useEffect(() => {
    if (!custodyValue) {
      updateSelfServiceData('dlCustodyType', 'ene');
    }
  }, [custodyValue, updateSelfServiceData]);

  useEffect(() => {
    if (!selfServiceData.dlGuardian1Name && fallbackGuardianName) {
      updateSelfServiceData('dlGuardian1Name', fallbackGuardianName);
    }
  }, [selfServiceData.dlGuardian1Name, fallbackGuardianName, updateSelfServiceData]);

  useEffect(() => {
    if (!selfServiceData.dlGuardian1Email && fallbackGuardianEmail) {
      updateSelfServiceData('dlGuardian1Email', fallbackGuardianEmail);
    }
  }, [selfServiceData.dlGuardian1Email, fallbackGuardianEmail, updateSelfServiceData]);

  useEffect(() => {
    contentApi.getByKey(contentKeys.koerekort.dl_parent_consent).then(content => {
      if (content?.body && content.body.length > 0) {
        setCopy(content.body);
      } else {
        setCopy(fallbackCopy);
      }
    });
  }, []);

  const clearSecondGuardian = () => {
    updateSelfServiceData('dlGuardian2Name', '');
    updateSelfServiceData('dlGuardian2Email', '');
    updateSelfServiceData('dlGuardian2Signed', false);
  };

  const handleCustodyChange = (value: 'ene' | 'med') => {
    updateSelfServiceData('dlCustodyType', value);
    if (value === 'ene') {
      updateSelfServiceData('dlNotifySecondGuardian', false);
      clearSecondGuardian();
    } else {
      updateSelfServiceData('dlNotifySecondGuardian', undefined);
    }
    setErrors(prev => ({ ...prev, custody: undefined }));
  };

  const validate = (): ErrorState => {
    const nextErrors: ErrorState = {};
    if (!custodyValue) {
      nextErrors.custody = 'Vælg forældremyndighed.';
    }
    return nextErrors;
  };

  const resetPrimarySignatureState = () => {
    updateSelfServiceData('dlGuardian1Signed', false);
    updateSelfServiceData('dlGuardian2Signed', false);
  };

  const handleNext = () => {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      return;
    }

    if (!isSharedCustody) {
      updateSelfServiceData('dlNotifySecondGuardian', false);
      clearSecondGuardian();
    }

    resetPrimarySignatureState();
    goNext();
  };

  return (
    <StepContainer title="Oplysninger om forældre/værger">
      <InfoBox type="info">
        {(copy ?? fallbackCopy).map((paragraph, index) => (
          <p key={index}>{paragraph}</p>
        ))}
      </InfoBox>

      <RadioGroup
        name="dlCustodyType"
        legend="Forældremyndighed"
        value={custodyValue}
        required
        options={custodyOptions}
        onChange={(value) => handleCustodyChange(value as 'ene' | 'med')}
      />
      {errors.custody && <span className="error-message" role="alert">{errors.custody}</span>}

      <div className="info-box" style={{ marginTop: '1.5rem' }}>
        <p><strong>Registreret underskriver:</strong></p>
        <p>{primaryGuardianSummary}</p>
        {isSharedCustody && (
          <p style={{ marginBottom: 0 }}>På næste trin kan du vælge, om vi skal sende en digital anmodning til forælder/værge 2.</p>
        )}
      </div>

      <NavigationButtons onBack={goBack} onNext={handleNext} />
    </StepContainer>
  );
};

export default DriverConsentGuardianStep;

